import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { translateTranscript } from '../../../../entities/post/api/translate';
import { CollapsibleBox, MutedBox, PostSectionTitle } from './post.section-components';

import type { Post } from '@/entities/post/types';
import { Button } from '@/shared/components/shadcn/button';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { cn } from '@/shared/utils/cn';

export const VideoAnalysis = ({ post }: { post: Post }) => {
  const { workspace } = useWorkspace();

  const [mode, setMode] = useState<'original' | 'translated'>('original');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState<{
    transcript: string;
    caption: string;
  } | null>(null);

  const handleTranslate = async () => {
    if (translatedContent) {
      setMode('translated');
    } else {
      setIsTranslating(true);
      const { transcript, caption } = await translateTranscript(post._id, workspace.language);
      setTranslatedContent({ transcript, caption });
      setIsTranslating(false);
      setMode('translated');
    }
  };

  const content =
    mode === 'translated'
      ? {
          transcript: translatedContent?.transcript || '',
          caption: translatedContent?.caption || '',
        }
      : {
          transcript: post.analyzation?.transcript || '',
          caption: post.analyzation?.caption || '',
        };

  if (!post.analyzation?.transcript && !post.analyzation?.caption) return null;

  return (
    <MutedBox>
      <CollapsibleBox
        trigger={<PostSectionTitle>Video analysis</PostSectionTitle>}
        actionNode={
          mode === 'translated' ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setMode('original');
              }}
            >
              See original
            </Button>
          ) : (
            <Button variant="ghost" size="icon" onClick={handleTranslate}>
              {isTranslating && <Loader2 className="size-4 animate-spin" />}
              Translate
            </Button>
          )
        }
        initialOpen
      >
        <div
          className={cn(
            'flex flex-col pt-6 transition-opacity duration-200',
            isTranslating && 'opacity-50',
          )}
        >
          <>
            <span className="text-muted-foreground pb-4 text-base font-medium">Transcript</span>
            <div className="text-sm wrap-break-word break-keep">{content.transcript || '-'}</div>
          </>
          {content.caption && (
            <>
              <div className="h-12" />
              <span className="text-muted-foreground mb-4 text-base font-medium">Caption</span>
              <div className="text-sm wrap-break-word break-keep">{content.caption}</div>
            </>
          )}
        </div>
      </CollapsibleBox>
    </MutedBox>
  );
};
